import type { PropertyListings } from '@/types/PropertyListings'
import React, { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    type CarouselApi,
} from "@/components/ui/carousel";
import { Badge } from '@/components/ui/badge';
import { Building2, Calendar, ChevronLeft, ChevronRight, DollarSign, FileText, Home, Mail, MapPin, Maximize, Ruler, Send, Warehouse } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { format } from 'date-fns';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import Helper from '@/lib/helper';
import { GoogleMap, Marker, Circle } from '@react-google-maps/api';
import { useGoogleMaps } from '@/components/GoogleMapsProvider';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import type { PropertyListingSharedAgents } from '@/types/PropertyListingSharedAgents';
import { useSelector } from 'react-redux';
import type { RootState } from '@/lib/redux/store';

interface _Details {
    listing: PropertyListings;
    size?: string;
}

const Details : React.FC<_Details> = ({
    listing,
    size
}) => {
    const agent = useSelector((state: RootState) => state.auth.agent);
    const [carouselApi, setCarouselApi] = useState<CarouselApi>();
    const [currentSlide, setCurrentSlide] = useState(0);
    // mediaType 5 is the agent intro video, shown in the agent block rather
    // than the property carousel.
    const galleryMedias = (listing?.medias ?? []).filter((m: any) => m.mediaType !== 5);
    const [areaUnit, setAreaUnit] = useState<'sqft' | 'acres'>('sqft');
    const { isLoaded } = useGoogleMaps();
    const zoningLabels: Record<number, string> = {
        1: 'Residential',
        2: 'Commercial',
        3: 'Industrial',
        4: 'Agricultural',
    };
    const landCategoryLabels: Record<number, string> = {
        1: 'Residential',
        2: 'Commercial',
        3: 'Industrial',
        4: 'Agricultural',
    };
    const zoningLabel = listing.zoningId ? zoningLabels[listing.zoningId] : null;
    const landCategoryLabel = listing.landTitleTypeId ? landCategoryLabels[listing.landTitleTypeId] : null;

    const formatDateTime = (date: string) =>
        new Intl.DateTimeFormat('en-MY', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(new Date(date));
    
    const SQFT_TO_ACRES = 43560;
    
    const convertArea = (sqft: number, toUnit: 'sqft' | 'acres') => {
        if (toUnit === 'acres') {
            return sqft / SQFT_TO_ACRES;
        }
        return sqft;
    };
    
    const getRoundedEstimate = (sqft: number) => {
        const magnitude = Math.pow(10, Math.floor(Math.log10(sqft)));
        return Math.floor(sqft / magnitude) * magnitude;
    };
    
    const getDisplayArea = (sqft: number, isEstimate: boolean = false) => {
        if (isEstimate) {
            if (areaUnit === 'acres') {
                const acres = sqft / SQFT_TO_ACRES;
                const magnitude = Math.pow(10, Math.floor(Math.log10(acres)));
                const rounded = Math.floor(acres / magnitude) * magnitude;
                return `${rounded.toFixed(magnitude < 1 ? Math.abs(Math.floor(Math.log10(magnitude))) : 0)}++`;
            }
            const rounded = getRoundedEstimate(sqft);
            return `${Helper.formatNumber(rounded)}++`;
        }
        const converted = convertArea(sqft, areaUnit);
        return areaUnit === 'acres' ? converted.toFixed(3) : Helper.formatNumber(converted);
    };
    
    const calculatePricePerUnit = (price: number, area: number) => {
        if (!area || area === 0) return null;
        const pricePerSqft = price / area;
        const pricePerUnit = areaUnit === 'acres' ? pricePerSqft * SQFT_TO_ACRES : pricePerSqft;
        return pricePerUnit;
    };

    useEffect(() => {
        if (!carouselApi) return;
        setCurrentSlide(carouselApi.selectedScrollSnap());

        carouselApi.on('select', () => {
            setCurrentSlide(carouselApi.selectedScrollSnap());
        });
    }, [carouselApi]);

    if(!isLoaded) {
        return null
    }

    return (
        <div className="space-y-6">
            <div className={`grid ${size === 'sm' ? 'grid-cols-1': 'lg:grid-cols-2'} gap-6`}>
                <div className="relative overflow-hidden rounded-lg border border-border">
                    <Carousel className="w-full" setApi={setCarouselApi}>
                        <CarouselContent>
                            {
                                galleryMedias.length > 0 ? (
                                    galleryMedias.map((media: any, index: number) => (
                                        <CarouselItem key={index}>
                                            <div
                                            className="relative aspect-video lg:h-[500px] w-full h-full bg-center bg-no-repeat bg-cover rounded-md overflow-hidden"
                                            style={{
                                                backgroundImage: `url(${import.meta.env.VITE_AWS_S3}/${media.path})`,
                                            }}>
                                                {index === 0 && (
                                                    <Badge className="absolute top-4 left-4 bg-background/80 text-foreground text-xs backdrop-blur-sm">
                                                        For {listing.forSaleOrRentId === 1 ? 'SALE' : 'RENT'}
                                                    </Badge>
                                                )}
                                            </div>
                                        </CarouselItem>
                                    ))
                                ) : (
                                    <CarouselItem>
                                        <div className="relative aspect-video lg:h-[500px] bg-muted flex items-center justify-center">
                                            <p className="text-muted-foreground text-sm">No images available</p>
                                        </div>
                                    </CarouselItem>
                                )
                            }
                        </CarouselContent>
                    </Carousel>
                    {
                        galleryMedias.length > 1 && (
                            <>
                                <button
                                onClick={() => carouselApi?.scrollPrev()}
                                className="cursor-pointer absolute left-3 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-background/60 backdrop-blur-sm border border-border/50 flex items-center justify-center hover:bg-background/80 transition-colors"
                                aria-label="Previous slide">
                                    <ChevronLeft size={18} className="text-foreground" />
                                </button>
                                <button
                                onClick={() => carouselApi?.scrollNext()}
                                className="cursor-pointer absolute right-3 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-background/60 backdrop-blur-sm border border-border/50 flex items-center justify-center hover:bg-background/80 transition-colors"
                                aria-label="Next slide">
                                    <ChevronRight size={18} className="text-foreground" />
                                </button>
                            </>
                        )
                    }
                    {
                        galleryMedias.length > 1 && (
                            <div className="bg-black p-3 absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 rounded-full">
                                {
                                    galleryMedias.map((_: any, index: number) => (
                                        <button
                                            key={index}
                                            onClick={() => carouselApi?.scrollTo(index)}
                                            className={`h-2 rounded-full transition-all cursor-pointer ${
                                                currentSlide === index
                                                    ? 'w-6 bg-white shadow-md'
                                                    : 'w-2 bg-gray-100 hover:bg-gray-300'
                                            }`}
                                            aria-label={`Go to slide ${index + 1}`}
                                        />
                                    ))
                                }
                            </div>
                        )
                    }
                </div>

                <div className="space-y-4">
                    <Card className="border-border">
                        <CardHeader className="pb-3">
                            <CardTitle className="flex items-center justify-between text-sm font-medium text-muted-foreground">
                                <span>Price</span>
                                <div className="flex items-center gap-2">
                                    <div className="flex bg-muted rounded-md p-0.5">
                                        <button
                                            onClick={() => setAreaUnit('sqft')}
                                            className={`px-2 py-0.5 text-xs rounded transition ${
                                                areaUnit === 'sqft' 
                                                    ? 'bg-background shadow-sm font-medium' 
                                                    : 'text-muted-foreground hover:text-foreground'
                                            }`}
                                        >
                                            sqft
                                        </button>
                                        <button
                                            onClick={() => setAreaUnit('acres')}
                                            className={`px-2 py-0.5 text-xs rounded transition ${
                                                areaUnit === 'acres' 
                                                    ? 'bg-background shadow-sm font-medium' 
                                                    : 'text-muted-foreground hover:text-foreground'
                                            }`}
                                        >
                                            acres
                                        </button>
                                    </div>
                                    <DollarSign className="text-muted-foreground" size={16} />
                                </div>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-3xl font-bold text-foreground">
                                RM {Helper.formatWithDecimal(Number(listing.price))}
                            </p>
                            {
                                listing.property.builtUp && calculatePricePerUnit(listing.price, Number(listing.property.builtUp)) && (
                                    <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
                                        <span>RM {Helper.formatWithDecimal(calculatePricePerUnit(listing.price, Number(listing.property.builtUp))!)} per</span>
                                        <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                                            listing.forSaleOrRentId === 1 
                                                ? 'bg-blue-100 text-blue-700' 
                                                : 'bg-green-100 text-green-700'
                                        }`}>
                                            {areaUnit}
                                        </span>
                                    </p>
                                )
                            }
                            {
                                listing.leaseTermId === 2 && listing.leaseTerm && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Lease Term: {listing.leaseTerm.label}
                                    </p>
                                )
                            }
                        </CardContent>
                    </Card>
                    <Card className="border-border">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium">Quick Details</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="flex items-center gap-2">
                                    <Building2 size={16} className="text-muted-foreground" />
                                    <div>
                                        <p className="text-xs text-muted-foreground">Property Type</p>
                                        <p className="text-sm font-medium">{listing.propertyType?.name}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Warehouse size={16} className="text-muted-foreground" />
                                    <div>
                                        <p className="text-xs text-muted-foreground">Sub Type</p>
                                        <p className="text-sm font-medium">{listing.propertySubType?.name}</p>
                                    </div>
                                </div>
                            </div>
                            <Separator />
                            <div className="grid grid-cols-2 gap-3">
                                {
                                    listing.tenure && (
                                        <div>
                                            <p className="text-xs text-muted-foreground">Tenure</p>
                                            <p className="text-sm font-medium">{listing.tenure.label}</p>
                                        </div>
                                    )
                                }
                                {
                                    listing.titleType && (
                                        <div>
                                            <p className="text-xs text-muted-foreground">Title Type</p>
                                            <p className="text-sm font-medium">{listing.titleType.label}</p>
                                        </div>
                                    )
                                }
                                {
                                    zoningLabel && (
                                        <div>
                                            <p className="text-xs text-muted-foreground">Zoning</p>
                                            <p className="text-sm font-medium">{zoningLabel}</p>
                                        </div>
                                    )
                                }
                                {
                                    landCategoryLabel && (
                                        <div>
                                            <p className="text-xs text-muted-foreground">Land Category</p>
                                            <p className="text-sm font-medium">{landCategoryLabel}</p>
                                        </div>
                                    )
                                }
                            </div>
                            {
                                listing.maintenanceFee && listing.maintenanceFee > 0 && (
                                    <>
                                        <Separator />
                                        <div>
                                            <p className="text-xs text-muted-foreground">Maintenance Fee</p>
                                            <p className="text-sm font-medium">RM {Helper.formatWithDecimal(listing.maintenanceFee)}/month</p>
                                        </div>
                                    </>
                                )
                            }
                            {[
                                listing.jupemNo,
                                listing.lotNo,
                                listing.districtId,
                                listing.subdistrictId,
                                listing.sizeOfLand,
                                listing.ownershipNumber,
                                listing.titleTypeCode
                            ].some(value => value !== null && value !== undefined && value !== 0) && (
                                <>
                                    <Separator />
                                    <h4 className="text-xs font-semibold text-foreground pt-1">Land Details</h4>
                                    <div className="grid grid-cols-2 gap-3">
                                        {
                                            listing.jupemNo && !size && (
                                                <div>
                                                    <p className="text-xs text-muted-foreground">JUPEM No</p>
                                                    <p className="text-sm font-medium">{listing.jupemNo}</p>
                                                </div>
                                            )
                                        }
                                        {
                                            listing.lotNo && !size && (
                                                <div>
                                                    <p className="text-xs text-muted-foreground">Lot No</p>
                                                    <p className="text-sm font-medium">{listing.lotNo}</p>
                                                </div>
                                            )
                                        }
                                        {
                                            listing.district && (
                                                <div>
                                                    <p className="text-xs text-muted-foreground">District</p>
                                                    <p className="text-sm font-medium">{listing.district.name}</p>
                                                </div>
                                            )
                                        }
                                        {
                                            listing.subdistrict && (
                                                <div>
                                                    <p className="text-xs text-muted-foreground">Sub-District</p>
                                                    <p className="text-sm font-medium">{listing.subdistrict.name}</p>
                                                </div>
                                            )
                                        }
                                        {
                                            listing.sizeOfLand && (
                                                <div>
                                                    <p className="text-xs text-muted-foreground">Size of Land</p>
                                                    <p className="text-sm font-medium">{getDisplayArea(Number(listing.sizeOfLand.replaceAll(',', '')), true)} {areaUnit}</p>
                                                </div>
                                            )
                                        }
                                        {
                                            listing.ownershipNumber && !size && (
                                                <div>
                                                    <p className="text-xs text-muted-foreground">Ownership Number</p>
                                                    <p className="text-sm font-medium">{listing.ownershipNumber}</p>
                                                </div>
                                            )
                                        }
                                        {
                                            listing.titleTypeCode && (
                                                <div>
                                                    <p className="text-xs text-muted-foreground">Land Title</p>
                                                    <p className="text-sm font-medium">{listing.titleTypeCode}</p>
                                                </div>
                                            )
                                        }
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
            <Card className="border-border">
                <CardHeader className="pb-4">
                    <CardTitle className="text-base font-medium flex items-center gap-2">
                        <Home size={18} />
                        Property Specifications
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className={`grid grid-cols-2 ${size === 'sm' ? '' : 'md:grid-cols-4'} gap-3`}>
                        {
                            listing.property.builtUp && (
                                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-md border border-border/50">
                                    <Maximize className="text-muted-foreground" size={16} />
                                    <div>
                                        <p className="text-xs text-muted-foreground">Built-up Area</p>
                                        <p className="text-sm font-semibold text-foreground">{getDisplayArea(Number(listing.builtUp), true)} {areaUnit}</p>
                                    </div>
                                </div>
                            )
                        }
                        {
                            listing.property.landArea && (
                                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-md border border-border/50">
                                    <Ruler className="text-muted-foreground" size={16} />
                                    <div>
                                        <p className="text-xs text-muted-foreground">Land Area</p>
                                        <p className="text-sm font-semibold text-foreground">{getDisplayArea(Number(listing.property.landArea), true)} {areaUnit}</p>
                                    </div>
                                </div>
                            )
                        }
                        {
                            listing.property.noOfBedrooms !== null && (
                                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-md border border-border/50">
                                    <Home className="text-muted-foreground" size={16} />
                                    <div>
                                        <p className="text-xs text-muted-foreground">Bedrooms</p>
                                        <p className="text-sm font-semibold text-foreground">{listing.property.noOfBedrooms}</p>
                                    </div>
                                </div>
                            )
                        }
                        {
                            listing.property.noOfBathrooms !== null && listing.property.noOfBathrooms !== 0 && (
                                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-md border border-border/50">
                                    <Home className="text-muted-foreground" size={16} />
                                    <div>
                                        <p className="text-xs text-muted-foreground">Bathrooms</p>
                                        <p className="text-sm font-semibold text-foreground">{listing.property.noOfBathrooms}</p>
                                    </div>
                                </div>
                            )
                        }
                        {
                            listing.property.noOfParkingSpots !== null && (
                                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-md border border-border/50">
                                    <Home className="text-muted-foreground" size={16} />
                                    <div>
                                        <p className="text-xs text-muted-foreground">Parking</p>
                                        <p className="text-sm font-semibold text-foreground">{listing.property.noOfParkingSpots}</p>
                                    </div>
                                </div>
                            )
                        }
                        {
                            listing.property.furnishing && (
                                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-md border border-border/50">
                                    <Home className="text-muted-foreground" size={16} />
                                    <div>
                                        <p className="text-xs text-muted-foreground">Furnishing</p>
                                        <p className="text-sm font-semibold text-foreground">{listing.property.furnishing.label}</p>
                                    </div>
                                </div>
                            )
                        }
                        {
                            Number(listing.property.yearOfCompletion) > 0 && (
                                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-md border border-border/50">
                                    <Calendar className="text-muted-foreground" size={16} />
                                    <div>
                                        <p className="text-xs text-muted-foreground">Year Built</p>
                                        <p className="text-sm font-semibold text-foreground">
                                            {listing.property.yearOfCompletion}
                                        </p>
                                    </div>
                                </div>
                            )
                        }
                        {
                            listing.availableDate && (
                                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-md border border-border/50">
                                    <Calendar className="text-muted-foreground" size={16} />
                                    <div>
                                        <p className="text-xs text-muted-foreground">Available From</p>
                                        <p className="text-sm font-semibold text-foreground">
                                            {format(new Date(listing.availableDate), 'MMM dd, yyyy')}
                                        </p>
                                    </div>
                                </div>
                            )
                        }
                    </div>
                    {[
                        listing.ceilingHeight,
                        listing.floorLoading,
                        listing.totalCargoLifts,
                        listing.totalPassengerLifts,
                        listing.maximumLiftCapacity,
                        listing.electricitySupply
                    ].some(value => value !== null && value !== undefined) && (
                        <>
                            <Separator className="my-4" />
                            <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                                <Warehouse size={16} />
                                Industrial Features
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {
                                listing.ceilingHeight != null && (
                                    <div className="p-3 bg-muted/30 rounded-md border border-border/50">
                                        <p className="text-xs text-muted-foreground">Ceiling Height</p>
                                        <p className="text-sm font-semibold text-foreground mt-1">{listing.ceilingHeight} {listing.ceilingHeightUnit}</p>
                                    </div>
                                )
                            }
                            {
                                listing.floorLoading != null && (
                                    <div className="p-3 bg-muted/30 rounded-md border border-border/50">
                                        <p className="text-xs text-muted-foreground">Floor Loading</p>
                                        <p className="text-sm font-semibold text-foreground mt-1">{listing.floorLoading} t/m²</p>
                                    </div>
                                )
                            }
                            {
                                listing.totalCargoLifts != null && (
                                    <div className="p-3 bg-muted/30 rounded-md border border-border/50">
                                        <p className="text-xs text-muted-foreground">Cargo Lifts</p>
                                        <p className="text-sm font-semibold text-foreground mt-1">{listing.totalCargoLifts}</p>
                                    </div>
                                )
                            }
                            {
                                listing.totalPassengerLifts != null && (
                                    <div className="p-3 bg-muted/30 rounded-md border border-border/50">
                                        <p className="text-xs text-muted-foreground">Passenger Lifts</p>
                                        <p className="text-sm font-semibold text-foreground mt-1">{listing.totalPassengerLifts}</p>
                                    </div>
                                )
                            }
                            {
                                listing.maximumLiftCapacity != null && (
                                    <div className="p-3 bg-muted/30 rounded-md border border-border/50">
                                        <p className="text-xs text-muted-foreground">Max Lift Capacity</p>
                                        <p className="text-sm font-semibold text-foreground mt-1">{listing.maximumLiftCapacity} kg</p>
                                    </div>
                                )
                            }
                            {
                                listing.electricitySupply != null && (
                                    <div className="p-3 bg-muted/30 rounded-md border border-border/50">
                                        <p className="text-xs text-muted-foreground">Electricity Supply</p>
                                        <p className="text-sm font-semibold text-foreground mt-1">{listing.electricitySupply} {listing.electricitySupplyUnit}</p>
                                    </div>
                                )
                            }
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>
            {
                listing.description && (
                    <Card className="border-border">
                        <CardHeader className="pb-4">
                            <CardTitle className="text-base font-medium flex items-center gap-2">
                                <FileText size={18} />
                                Description
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">
                                {listing.description}
                            </p>
                        </CardContent>
                    </Card>
                )
            }
            {
                ((listing.createdBy === agent.id) || listing.sharedAgents.find((x: PropertyListingSharedAgents) => x.agentId === agent.id)) && (
                    <Card className="border-border">
                        <CardHeader className="pb-4">
                            <CardTitle className="text-base font-medium flex items-center gap-2">
                                <MapPin size={18} />
                                Location Details
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid md:grid-cols-3 gap-4">
                                {
                                    listing.township && (
                                        <div>
                                            <p className="text-xs text-muted-foreground">Township</p>
                                            <p className="text-sm font-medium text-foreground mt-1">{listing.township}</p>
                                        </div>
                                    )
                                }
                                {
                                    listing.city && (
                                        <div>
                                            <p className="text-xs text-muted-foreground">City</p>
                                            <p className="text-sm font-medium text-foreground mt-1">{listing.city.name}</p>
                                        </div>
                                    )
                                }
                                {
                                    listing.street && (
                                        <div>
                                            <p className="text-xs text-muted-foreground">Street</p>
                                            <p className="text-sm font-medium text-foreground mt-1">{listing.street}</p>
                                        </div>
                                    )
                                }
                                {
                                    listing.state && (
                                        <div>
                                            <p className="text-xs text-muted-foreground">State</p>
                                            <p className="text-sm font-medium text-foreground mt-1">{listing.state.name}</p>
                                        </div>
                                    )
                                }
                                {
                                    listing.postcode && (
                                        <div>
                                            <p className="text-xs text-muted-foreground">Postcode</p>
                                            <p className="text-sm font-medium text-foreground mt-1">{listing.postcode}</p>
                                        </div>
                                    )
                                }
                                {
                                    listing.lat && listing.lng && (
                                        <div>
                                            <p className="text-xs text-muted-foreground">Coordinates</p>
                                            <p className="text-sm font-medium text-foreground mt-1">
                                                {listing.lat}, {listing.lng}
                                            </p>
                                        </div>
                                    )
                                }
                                {
                                    listing.rawAddress && (
                                        <div>
                                            <p className="text-xs text-muted-foreground">Address</p>
                                            <p className="text-sm font-medium text-foreground mt-1">{listing.rawAddress}</p>
                                        </div>
                                    )
                                }
                            </div>
                            {/* <div className='rounded-xl pt-5 overflow-hidden'>
                                {!listing.useTrueLocation && listing.radius && (
                                    <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                        <p className="text-xs text-blue-700 flex items-center gap-1">
                                            <MapPin className="h-3 w-3 flex-shrink-0" />
                                            <span>Approximate location shown with a radius of <strong>{listing.radius}m</strong> ({(listing.radius / 1000).toFixed(2)}km) for privacy</span>
                                        </p>
                                    </div>
                                )}
                                <GoogleMap
                                center={{
                                    lat: Number(listing.lat),
                                    lng: Number(listing.lng),
                                }}
                                zoom={15}
                                mapContainerStyle={{ 
                                    width: '100%', 
                                    height: '300px',
                                    borderRadius: '0.675rem'
                                }}
                                options={{
                                    disableDefaultUI: true,
                                    zoomControl: false,
                                    gestureHandling: 'greedy',
                                    styles: [
                                        { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                                        { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                                        { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] }
                                    ]
                                }}>
                                    <Circle
                                    center={{ lat: Number(listing.lat), lng: Number(listing.lng) }}
                                    radius={Number(listing.radius) || 50}
                                    options={{
                                        fillColor: '#4285F4',
                                        fillOpacity: 0.2,
                                        strokeColor: '#4285F4',
                                        strokeOpacity: 0.8,
                                        strokeWeight: 2,
                                        editable: false,
                                        draggable: false,
                                    }} />
                                </GoogleMap>
                            </div> */}
                        </CardContent>
                    </Card>
                )
            }
            {
                listing.agent && (
                    <Card className="border-border">
                        <CardHeader className="pb-4">
                            <CardTitle className="text-base font-medium">Listed By</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center gap-4">
                                <Avatar className="h-16 w-16">
                                    <AvatarImage src={listing.agent.avatar ? `${import.meta.env.VITE_AWS_S3}/${listing.agent.avatar}` : undefined} />
                                    <AvatarFallback className="text-lg">
                                        {listing.agent.firstName?.[0]}{listing.agent.lastName?.[0]}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="flex-1">
                                    <h4 className="font-semibold text-foreground">
                                        {listing.agent.firstName} {listing.agent.lastName}
                                    </h4>
                                    <p className="text-sm text-muted-foreground">REN No: {listing.agent.renNo}</p>
                                </div>
                            </div>
                            {
                                !size && listing.sharedAgents && listing.sharedAgents.length > 0 && (
                                    <>
                                        <hr className='mt-5'/>
                                        {listing.sharedAgents.map((agent: any) => (
                                            <div className="flex items-center gap-4 mt-3" key={agent.id}>
                                                <Avatar className="h-12 w-12">
                                                    <AvatarImage src={agent.avatar ? `${import.meta.env.VITE_AWS_S3}/${agent.agent.avatar}` : undefined} />
                                                    <AvatarFallback className="text-lg">
                                                        {agent.agent.firstName?.[0]}{agent.agent.lastName?.[0]}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="flex-1">
                                                    <h4 className="font-semibold text-sm">
                                                        {agent.agent.firstName} {agent.agent.lastName}
                                                        <Badge className='ml-2'>{agent.type == 1 ? 'Co-Agency' : 'Co-Share'}</Badge>
                                                    </h4>
                                                    <p className="text-xs">REN No: {agent.agent.renNo}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )
                            }
                        </CardContent>
                    </Card>
                )
            }
            {
                (
                    listing.createdBy === agent.id ||
                    listing.sharedAgents?.some(
                      (x: PropertyListingSharedAgents) => x.agentId === agent.id
                    )
                  ) &&
                  listing.owners &&
                  listing.owners.length > 0 && (
                    <Card className="border-border">
                        <CardHeader className="pb-4">
                            <CardTitle className="text-base font-medium">Owners</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                {listing.owners.map((owner: any, index: number) => (
                                    <div key={owner.id || index} className="flex items-center gap-3 p-3">
                                        <Avatar className="h-12 w-12 flex-shrink-0">
                                            <AvatarImage src={owner.owner?.avatar ? `${import.meta.env.VITE_AWS_S3}/${owner.owner?.avatar}` : undefined} />
                                            <AvatarFallback className="text-sm">
                                                {owner.owner?.firstName?.[0]}{owner.owner?.lastName?.[0]}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-semibold text-sm text-foreground truncate">
                                                {owner.owner?.firstName} {owner.owner?.lastName}
                                            </h4>
                                            {owner.owner?.email && (
                                                <p className="text-xs text-muted-foreground truncate">
                                                    {owner.owner?.email}
                                                </p>
                                            )}
                                            {owner.status === null && owner.requestSentAt && (
                                                <p className="text-xs flex items-center text-muted-foreground">
                                                    <Mail className="h-3 w-3 mr-1" />
                                                    {formatDateTime(owner.requestSentAt)}
                                                </p>
                                            )}
                                            {owner.status !== null && Number(owner.status) === 0 && owner.requestSentAt && (
                                                <TooltipProvider>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <p className="text-xs flex items-center text-red-500 cursor-help">
                                                                <Mail className="h-3 w-3 mr-1" />
                                                                {formatDateTime(owner.requestSentAt)}
                                                            </p>
                                                        </TooltipTrigger>
                                                        {owner.reason && (
                                                            <TooltipContent side="top" className="max-w-xs text-xs">
                                                                {owner.reason}
                                                            </TooltipContent>
                                                        )}
                                                    </Tooltip>
                                                </TooltipProvider>
                                            )}
                                            {owner.status !== null && Number(owner.status) === 1 && (
                                                <p className="text-xs flex items-center text-green-600">
                                                    <Mail className="h-3 w-3 mr-1" />
                                                    {formatDateTime(owner.updatedByOwnerAt)}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )
            }
        </div>
    )
}

export default Details
